import React, { useCallback, useEffect, useRef, useState } from 'react';
import centralAPI from '../../CentralAPIHandler/CentralAPIHandler';
import { IMAGE_COMPRESSOR } from '../../utils/imageCompressor';
import Loader from '../common/Loader';

// Main panel is stretched to fill the viewport via CSS (width/height: 100%,
// object-fit: contain), so it needs a much larger source than the small
// before-inset thumbnail to avoid looking blurry.
const MAIN_RESOLUTION = '1600x0';
const INSET_RESOLUTION = '700x0';
const ZOOM_MIN = 50;
const ZOOM_MAX = 300;
const ZOOM_STEP = 10;

const getQueryParams = () => {
    const queryParams = new URLSearchParams(window.location.search);
    const aSpinParam = queryParams.get('a_spin');
    return {
        skuId: queryParams.get('sku_id'),
        subSkuId: queryParams.get('sub_sku_id'),
        imageCategory: queryParams.get('image_category'),
        isConsole: queryParams.get('isConsole') === 'true',
        // Mirrors View360.js: auto-spin defaults on unless a_spin is explicitly false/0.
        initialAutoSpin: aSpinParam === null ? true : aSpinParam === 'true' || aSpinParam === '1',
        spinMotion: queryParams.get('spin_motion') ? JSON.parse(queryParams.get('spin_motion')) : false,
    };
};

const fetchVehicleFrames = async (skuId, imageCategory, isConsole) => {
    if (!skuId) return [];

    const URL = `${process.env.REACT_APP_IMAGECREW_NODE_PROJ_PREFIX}/api/v1/hotspot/get-sku-image-data`;
    const response = await centralAPI.handleGetRequest(URL, {
        skuId,
        imageCategory,
        type: 'output',
    });

    let skuDetails = response?.skuDetails || [];
    if (!isConsole) {
        skuDetails = skuDetails.filter((image) => image?.ai_generated !== true);
    }

    const frames = skuDetails.map((image, index) => ({
        angle: typeof image.angle === 'number' && !Number.isNaN(image.angle) ? image.angle : null,
        frameInt: typeof image.FRAME_INT === 'number' && !Number.isNaN(image.FRAME_INT) ? image.FRAME_INT : index,
        afterSrc: IMAGE_COMPRESSOR({
            url: image.output_image_hres_url || image.input_image_hres_url,
            resolution: MAIN_RESOLUTION,
        }),
        beforeSrc: IMAGE_COMPRESSOR({
            url: image.input_image_hres_url || image.output_image_hres_url,
            resolution: INSET_RESOLUTION,
        }),
    }));

    // Only used for auto-spin direction (View360.js:3537), never for manual
    // drag direction — that's hardcoded in move() independent of rotation.
    const rotationSign = response?.sequenceData?.rotation === 'clockwise' ? 1 : -1;

    return { frames, rotationSign };
};

// Normalizes a frame's rotation position to 0..1 so frame sets with different
// counts/spacing can still be matched against each other. `angle` (degrees)
// is the real signal; when the API returns it as null, FRAME_INT (a plain
// sequence index) is used as a stand-in, normalized against the array length.
const getFramePosition = (frame, totalFrames) => {
    if (typeof frame?.angle === 'number' && !Number.isNaN(frame.angle)) {
        return frame.angle / 360;
    }
    if (typeof frame?.frameInt === 'number' && !Number.isNaN(frame.frameInt)) {
        return frame.frameInt / (totalFrames || 1);
    }
    return 0;
};

const findNearestByPosition = (frames, targetPosition) => {
    if (!frames.length) return null;
    let nearest = frames[0];
    let minDiff = Infinity;
    frames.forEach((frame) => {
        const framePosition = getFramePosition(frame, frames.length);
        const rawDiff = Math.abs(framePosition - targetPosition);
        const diff = Math.min(rawDiff, 1 - rawDiff);
        if (diff < minDiff) {
            minDiff = diff;
            nearest = frame;
        }
    });
    return nearest;
};

// The main image is shown with object-fit: contain, so unless its aspect
// ratio exactly matches the panel's, it's letterboxed (or pillarboxed)
// inside it. This computes that inner rect so the inset can anchor to the
// actual visible photo rather than the panel's outer edge.
const getContainRect = (containerWidth, containerHeight, imageWidth, imageHeight) => {
    if (!containerWidth || !containerHeight || !imageWidth || !imageHeight) return null;

    const containerRatio = containerWidth / containerHeight;
    const imageRatio = imageWidth / imageHeight;

    let renderWidth = containerWidth;
    let renderHeight = containerHeight;
    if (imageRatio > containerRatio) {
        renderHeight = containerWidth / imageRatio;
    } else {
        renderWidth = containerHeight * imageRatio;
    }

    return {
        offsetX: (containerWidth - renderWidth) / 2,
        offsetY: (containerHeight - renderHeight) / 2,
        renderWidth,
        renderHeight,
    };
};

/**
 * Renders the after (AI-studio) spin for sku_id as the main view, with a
 * floating BEFORE inset showing the raw photo for sub_sku_id at whichever
 * rotation position the main spin is currently on. The two frame sets are
 * matched by normalized position (angle, or FRAME_INT when angle is null)
 * rather than by index, since they need not share a frame count.
 */
const JointView360 = () => {
    const [primaryFrames, setPrimaryFrames] = useState([]);
    const [subFrames, setSubFrames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [progress, setProgress] = useState(0);
    const [beforeZoom, setBeforeZoom] = useState(100);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [panelSize, setPanelSize] = useState({ width: 0, height: 0 });
    const [imageNaturalSize, setImageNaturalSize] = useState({ width: 0, height: 0 });
    const [beforeNaturalSize, setBeforeNaturalSize] = useState({ width: 0, height: 0 });
    const [autoSpin, setAutoSpin] = useState(() => getQueryParams().initialAutoSpin);
    const [spinMotion] = useState(() => getQueryParams().spinMotion);
    const [primaryRotationSign, setPrimaryRotationSign] = useState(1);

    const wrapperRef = useRef(null);
    const mainPanelRef = useRef(null);
    const beforeImageWrapperRef = useRef(null);
    const mainDragState = useRef({ isDragging: false, startX: 0, startProgress: 0 });
    const insetDragState = useRef({ isDragging: false, startX: 0, startProgress: 0 });

    useEffect(() => {
        const { skuId, subSkuId, imageCategory, isConsole } = getQueryParams();

        if (!skuId || !subSkuId) {
            setError('Both sku_id and sub_sku_id are required for the joint view.');
            setLoading(false);
            return;
        }

        Promise.all([
            fetchVehicleFrames(skuId, imageCategory, isConsole),
            fetchVehicleFrames(subSkuId, imageCategory, isConsole),
        ])
            .then(([primary, sub]) => {
                if (!primary.frames.length || !sub.frames.length) {
                    setError('Could not load images for one or both vehicles.');
                    return;
                }
                setPrimaryFrames(primary.frames);
                setSubFrames(sub.frames);
                setPrimaryRotationSign(primary.rotationSign);
            })
            .catch(() => setError('Failed to load joint view images.'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    useEffect(() => {
        // mainPanelRef only mounts once `loading` is false (the loading state
        // renders <Loader/> instead), so this must re-run when that flips —
        // otherwise it bails out once on mount, before the node exists, and
        // panelSize stays {0,0} forever, silently disabling image-relative
        // positioning below (it'd fall back to the plain CSS default).
        if (!mainPanelRef.current) return undefined;
        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) return;
            const { width, height } = entry.contentRect;
            setPanelSize({ width, height });
        });
        observer.observe(mainPanelRef.current);
        return () => observer.disconnect();
    }, [loading]);

    // Mirrors View360.js's updateIndexValue interval timing exactly:
    // frames < 12 uses 400ms; otherwise 75ms under spin_motion, else 150ms.
    // Runs until the user starts a manual drag.
    useEffect(() => {
        if (!autoSpin || !primaryFrames.length) return undefined;
        const intervalSpan = primaryFrames.length < 12 ? 400 : spinMotion ? 75 : 150;
        const id = setInterval(() => {
            setProgress((prev) => {
                let next = (prev + primaryRotationSign * (1 / primaryFrames.length)) % 1;
                if (next < 0) next += 1;
                return next;
            });
        }, intervalSpan);
        return () => clearInterval(id);
    }, [autoSpin, primaryFrames.length, primaryRotationSign, spinMotion]);

    const handleMainImageLoad = (e) => {
        const { naturalWidth, naturalHeight } = e.target;
        setImageNaturalSize((prev) =>
            prev.width === naturalWidth && prev.height === naturalHeight
                ? prev
                : { width: naturalWidth, height: naturalHeight }
        );
    };

    const handleBeforeImageLoad = (e) => {
        const { naturalWidth, naturalHeight } = e.target;
        setBeforeNaturalSize((prev) =>
            prev.width === naturalWidth && prev.height === naturalHeight
                ? prev
                : { width: naturalWidth, height: naturalHeight }
        );
    };

    const frameForProgress = (frames, p) => {
        if (!frames.length) return null;
        const index = Math.floor(p * frames.length) % frames.length;
        return frames[index];
    };

    // Both the main panel and the before-inset drive the same `progress` value,
    // each measuring drag delta against its own container width so drag speed
    // feels natural in either panel, but always writing to one shared rotation.
    const updateProgressFromDelta = useCallback((deltaX, containerRef, dragStateRef) => {
        const width = containerRef.current?.offsetWidth || 1;
        // View360.js's own move() increments imageIndex when the pointer moves
        // left and decrements it when it moves right (View360.js:2046-2052) —
        // i.e. index correlates with -deltaX, not +deltaX. Match that here so
        // the same sku_id spins the same direction in both places.
        const deltaProgress = -deltaX / width;
        let next = (dragStateRef.current.startProgress + deltaProgress) % 1;
        if (next < 0) next += 1;
        setProgress(next);
    }, []);

    // Pointer capture keeps move/up events going to the element that started the
    // drag even once the cursor leaves its bounds — needed because the before
    // inset is small enough that a normal drag exits it almost immediately.
    const handlePointerDown = (e, containerRef, dragStateRef) => {
        e.currentTarget.setPointerCapture?.(e.pointerId);
        dragStateRef.current = { isDragging: true, startX: e.clientX, startProgress: progress };
        // Matches View360.js's move(), which turns automatedImageSpin off on
        // the first manual drag rather than resuming it afterward.
        setAutoSpin(false);
    };

    const handlePointerMove = (e, containerRef, dragStateRef) => {
        if (!dragStateRef.current.isDragging) return;
        updateProgressFromDelta(e.clientX - dragStateRef.current.startX, containerRef, dragStateRef);
    };

    const handlePointerUp = (e, dragStateRef) => {
        dragStateRef.current.isDragging = false;
        e?.currentTarget?.releasePointerCapture?.(e.pointerId);
    };

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            wrapperRef.current?.requestFullscreen?.();
        } else {
            document.exitFullscreen?.();
        }
    };

    const zoomOut = () => setBeforeZoom((prev) => Math.max(ZOOM_MIN, prev - ZOOM_STEP));
    const zoomIn = () => setBeforeZoom((prev) => Math.min(ZOOM_MAX, prev + ZOOM_STEP));

    if (loading) {
        return <Loader />;
    }

    if (error) {
        return <div className="flex items-center justify-center h-full p-6 text-center text-red-500 text-sm">{error}</div>;
    }

    const primaryFrame = frameForProgress(primaryFrames, progress);
    const beforeFrame = primaryFrame
        ? findNearestByPosition(subFrames, getFramePosition(primaryFrame, primaryFrames.length))
        : null;

    const imageRect = getContainRect(panelSize.width, panelSize.height, imageNaturalSize.width, imageNaturalSize.height);
    const insetInlineStyle = {
        ...(imageRect && {
            top: `${imageRect.offsetY + 16}px`,
            left: `${imageRect.offsetX + 16}px`,
            // 30% of the main photo's own rendered height, not the panel's —
            // those differ whenever the photo is letterboxed inside the panel.
            height: `${imageRect.renderHeight * 0.3}px`,
        }),
        // Sizes the card to the before-photo's own aspect ratio instead of a
        // fixed box, so it never crops/stretches whatever shape that photo is.
        ...(beforeNaturalSize.width &&
            beforeNaturalSize.height && {
                aspectRatio: `${beforeNaturalSize.width} / ${beforeNaturalSize.height}`,
            }),
    };

    return (
        <div ref={wrapperRef} className="relative w-full h-full bg-white overflow-hidden">
            <div
                ref={mainPanelRef}
                className="w-full h-full flex items-center justify-center cursor-grab active:cursor-grabbing"
                onPointerDown={(e) => handlePointerDown(e, mainPanelRef, mainDragState)}
                onPointerMove={(e) => handlePointerMove(e, mainPanelRef, mainDragState)}
                onPointerUp={(e) => handlePointerUp(e, mainDragState)}
                onPointerCancel={(e) => handlePointerUp(e, mainDragState)}
                style={{
                    '-webkit-user-select': 'none',
                    'user-select': 'none',
                    'touch-action': 'none',
                }}
            >
                {primaryFrame && (
                    <img
                        src={primaryFrame.afterSrc}
                        alt="Vehicle"
                        draggable={false}
                        className="w-full h-full object-contain pointer-events-none"
                        onLoad={handleMainImageLoad}
                    />
                )}

                {beforeFrame && (
                    <div className="absolute top-8 left-8 h-[30%] w-auto overflow-hidden rounded-[14px] shadow-lg z-10" style={insetInlineStyle}>
                        <div className="h-full w-full relative">
                            <div className="flex absolute top-3 left-3 z-[1] bg-[#11182a] items-center gap-2 opacity-80 py-[3px] px-1.5 rounded-[999px] text-white text-[9px] font-semibold tracking-wider">
                                <span className="w-2 h-2 bg-[#22c55e] rounded-full shrink-0" />
                                <span className="text-[9px] font-semibold tracking-wider text-white-default">BEFORE</span>
                            </div>
                            <div
                                ref={beforeImageWrapperRef}
                                className="w-full h-full overflow-hidden bg-black cursor-grab active:cursor-grabbing"
                                style={{
                                    '-webkit-user-select': 'none',
                                    'user-select': 'none',
                                    'touch-action': 'none',
                                }}
                                onPointerDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handlePointerDown(e, beforeImageWrapperRef, insetDragState);
                                }}
                                onPointerMove={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handlePointerMove(e, beforeImageWrapperRef, insetDragState);
                                }}
                                onPointerUp={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handlePointerUp(e, insetDragState);
                                }}
                                onPointerCancel={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handlePointerUp(e, insetDragState);
                                }}
                            >
                                <img
                                    src={beforeFrame.beforeSrc}
                                    alt="Before"
                                    draggable={false}
                                    className="w-full h-full object-contain pointer-events-none"

                                    style={{ transform: `scale(${beforeZoom / 100})`, '-webkit-user-select': 'none', 'user-select': 'none', 'touch-action': 'none', }}
                                    onLoad={handleBeforeImageLoad}
                                />
                            </div>
                        </div>
                        {/* <div className={Styles.zoomControls}>
                            <button type="button" className={Styles.zoomBtn} onClick={zoomOut} aria-label="Zoom out">
                                &minus;
                            </button>
                            <span>{beforeZoom}%</span>
                            <button type="button" className={Styles.zoomBtn} onClick={zoomIn} aria-label="Zoom in">
                                +
                            </button>
                        </div> */}
                    </div>
                )}
            </div>

            <div className='absolute bottom-0 left-0 -z-[1] hidden'>
                {
                    primaryFrames?.map((frame) => (
                        <div key={frame.id} className='w-full h-full'>
                            <img src={frame.afterSrc} alt="Vehicle" className='w-full h-full object-contain' />
                        </div>
                    ))
                }
                {
                    subFrames?.map((frame) => (
                        <div key={frame.id} className='w-full h-full'>
                            <img src={frame.beforeSrc} alt="Before" className='w-full h-full object-contain' />
                        </div>
                    ))
                }
            </div>

        </div>
    );
};

export default JointView360;
